/**
 * Freestar ad provider.
 *
 * Freestar manages GPT, Prebid, Amazon APS, and other demand sources internally.
 * We just need to load their script, then tell them about our placements.
 *
 * Freestar API reference:
 *   - freestar.queue.push(fn)   — queue work until their lib is ready
 *   - freestar.newAdSlots(arr)  — declare new placements
 *   - freestar.deleteAdSlots(id) — remove a placement
 *   - freestar.newStickyFooter(id) — for sticky banners (not used here)
 */

const MAX_ADS_PER_PAGE = 20;

export class FreestarProvider {
  /** @type {Map<string, HTMLElement>} slotId → <ad-slot> element */
  #slots = new Map();
  #config;

  /**
   * @param {object} config — server-injected ad config
   * @param {string} config.freestar.publisherId — e.g. "meetme-com"
   * @param {string} config.brandPrefix — e.g. "MeetMe"
   * @param {number} [config.refreshInterval] — auto-refresh in seconds
   */
  constructor(config) {
    this.#config = config;
  }

  initialize() {
    const { publisherId } = this.#config.freestar;

    // Freestar command queue (mirrors their snippet pattern)
    globalThis.freestar = globalThis.freestar || {};
    globalThis.freestar.queue = globalThis.freestar.queue || [];
    globalThis.freestar.config = globalThis.freestar.config || {};
    globalThis.freestar.config.enabled_slots = [];

    // Load their publisher script
    const script = document.createElement("script");
    script.src = `https://a.pub.network/${publisherId}/pubfig.min.js`;
    script.async = true;
    document.head.appendChild(script);
  }

  /**
   * Called by <ad-slot> when it enters the viewport.
   * @param {HTMLElement} slotEl — the <ad-slot> custom element
   */
  registerSlot(slotEl) {
    const slotId = slotEl.slotId;
    if (!slotId || this.#slots.size >= MAX_ADS_PER_PAGE) return;
    if (this.#slots.has(slotId)) return;

    this.#slots.set(slotId, slotEl);

    const placementName = `${this.#config.brandPrefix}_${slotEl.unitPath}`;

    globalThis.freestar.queue.push(() => {
      globalThis.freestar.newAdSlots([
        {
          placementName,
          slotId,
        },
      ]);
    });
  }

  /**
   * Called by <ad-slot> on disconnect.
   * @param {HTMLElement} slotEl
   */
  unregisterSlot(slotEl) {
    const slotId = slotEl.slotId;
    if (!this.#slots.has(slotId)) return;
    this.#slots.delete(slotId);

    globalThis.freestar.queue.push(() => {
      globalThis.freestar.deleteAdSlots(slotId);
    });
  }
}
